import { ArrowLeft, CreditCard, Pencil, UserPlus } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAvatar } from "@/shared/components/data-display/ColocaAvatar";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import useTranslations from "@/shared/lang/useTranslations";

import type { OrganizationDetailResponse } from "../types/clients-api.types";
import { CountryLabel } from "./CountryLabel";

export interface OrganizationContextHeaderProps {
  organization: OrganizationDetailResponse | null;
  isLoading: boolean;
  onBack: () => void;
  onEditOrganization?: () => void;
  onViewAccounts?: () => void;
  onCreateClient?: () => void;
}

/** Drill-down context header: back affordance, organization identity, summary chips, and actions. */
export function OrganizationContextHeader({
  organization,
  isLoading,
  onBack,
  onEditOrganization,
  onViewAccounts,
  onCreateClient,
}: OrganizationContextHeaderProps) {
  const { t } = useTranslations("clients");

  return (
    <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex min-w-0 items-center gap-4">
        <ColocaButton variant="ghost" size="sm" icon={ArrowLeft} onClick={onBack}>
          {t("drilldown_back")}
        </ColocaButton>

        {isLoading || !organization ? (
          <div className="flex items-center gap-3">
            <ColocaSkeleton shape="circle" className="size-10" />
            <div className="flex flex-col gap-1.5">
              <ColocaSkeleton className="h-4 w-44" />
              <ColocaSkeleton className="h-3 w-64" />
            </div>
          </div>
        ) : (
          <div className="flex min-w-0 items-center gap-3">
            <ColocaAvatar name={organization.name} size="lg" />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="truncate text-lg font-semibold tracking-tight text-foreground">
                  {organization.name}
                </h1>
                {organization.type && (
                  <ColocaBadge variant={organization.type === "business" ? "secondary" : "default"}>
                    {organization.type === "business" ? t("table_type_empresa") : t("table_type_individual")}
                  </ColocaBadge>
                )}
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
                <span className="truncate" title={organization.email}>
                  {organization.email}
                </span>
                <span aria-hidden="true">·</span>
                <CountryLabel code={organization.country} />
                <span aria-hidden="true">·</span>
                <span>
                  <strong className="font-medium text-foreground tabular-nums">
                    {organization.clients.length.toLocaleString()}
                  </strong>{" "}
                  {t("orgs_count_clients")}
                </span>
                <span aria-hidden="true">·</span>
                <span>
                  <strong className="font-medium text-foreground tabular-nums">
                    {organization.accountIds.length.toLocaleString()}
                  </strong>{" "}
                  {t("orgs_count_accounts")}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {onViewAccounts && (
          <ColocaButton variant="default" icon={CreditCard} onClick={onViewAccounts}>
            {t("drilldown_accounts")}
          </ColocaButton>
        )}
        {onEditOrganization && (
          <ColocaButton variant="default" icon={Pencil} onClick={onEditOrganization}>
            {t("drilldown_edit_organization")}
          </ColocaButton>
        )}
        {onCreateClient && (
          <ColocaButton variant="primary" icon={UserPlus} onClick={onCreateClient}>
            {t("button_create_client")}
          </ColocaButton>
        )}
      </div>
    </header>
  );
}
